import {
  MessarioAiFetchFailure,
  requestMessarioAiGenerate
} from '@lib/messarioAi/api';
import {
  clearMessarioAiGenerateBundleCache,
  ensureMessarioAiGenerateBundle
} from '@lib/messarioAi/generateBundleCache';
import {
  MessarioAiBundleEnabled,
  MessarioAiContextMessage,
  MessarioAiDraftResponse,
  MessarioAiGenerateBundleEnabled,
  MessarioAiGenerateDraftPayload
} from '@lib/messarioAi/types';

type GenerateArgs = {
  token: string,
  panelOrigin: string,
  externalUserId: string,
  peerId: string,
  messages: MessarioAiContextMessage[],
  oneShot?: string,
  metadata: MessarioAiBundleEnabled,
  fetchValue?: typeof fetch,
  storage?: Storage
};

export function isOutdatedAiBundleFailure(err: unknown): boolean {
  if(!(err instanceof MessarioAiFetchFailure) || err.errorCode === 'attempt_in_progress') {
    return false;
  }

  return err.errorCode === 'outdated_ai_bundle' ||
    (err.status === 409 && err.source === 'panel');
}

function newClientAttemptId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if(uuid) {
    return uuid;
  }

  return `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`.slice(0, 64);
}

function buildGeneratePayload(
  peerId: string,
  messages: MessarioAiContextMessage[],
  oneShot: string | undefined,
  bundle: MessarioAiGenerateBundleEnabled,
  clientAttemptId: string
): MessarioAiGenerateDraftPayload {
  const payload: MessarioAiGenerateDraftPayload = {
    peer_id: peerId,
    messages,
    prompt_revision: bundle.prompt_revision,
    variable_definitions_revision: bundle.variable_definitions_revision,
    model: bundle.model,
    client_attempt_id: clientAttemptId
  };
  if(oneShot) {
    payload.one_shot_instruction = oneShot;
  }

  return payload;
}

async function postGenerateWithNetworkRetries(
  token: string,
  panelOrigin: string,
  payload: MessarioAiGenerateDraftPayload,
  fetchValue: typeof fetch
): Promise<MessarioAiDraftResponse> {
  let lastError: unknown;
  for(let attempt = 0; attempt < 3; attempt++) {
    try {
      return await requestMessarioAiGenerate(token, panelOrigin, payload, fetchValue);
    } catch(err) {
      lastError = err;
      if(isOutdatedAiBundleFailure(err)) {
        throw err;
      }
      const isNetwork = err instanceof MessarioAiFetchFailure && err.kind === 'network';
      if(!isNetwork || attempt === 2) {
        if(isNetwork) {
          throw new MessarioAiFetchFailure(
            'network',
            'Не удалось связаться с Messario Panel для генерации черновика. Повторите Generate.',
            err.status,
            undefined,
            'panel'
          );
        }
        throw err;
      }
    }
  }

  throw lastError;
}

export async function generateMessarioAiDraftLocal(args: GenerateArgs): Promise<MessarioAiDraftResponse> {
  const fetchValue = args.fetchValue ?? fetch;
  const storage = args.storage ?? (typeof window !== 'undefined' ? window.sessionStorage : undefined);
  if(!storage) {
    throw new MessarioAiFetchFailure('configuration', 'sessionStorage is unavailable.');
  }

  let bundle = await ensureMessarioAiGenerateBundle(
    args.token,
    args.panelOrigin,
    args.externalUserId,
    args.metadata,
    fetchValue,
    storage
  );
  const clientAttemptId = newClientAttemptId();
  const post = (current: MessarioAiGenerateBundleEnabled) => postGenerateWithNetworkRetries(
    args.token,
    args.panelOrigin,
    buildGeneratePayload(args.peerId, args.messages, args.oneShot, current, clientAttemptId),
    fetchValue
  );

  try {
    return await post(bundle);
  } catch(err) {
    if(!isOutdatedAiBundleFailure(err)) {
      throw err;
    }

    clearMessarioAiGenerateBundleCache(args.externalUserId, storage);
    bundle = await ensureMessarioAiGenerateBundle(
      args.token,
      args.panelOrigin,
      args.externalUserId,
      args.metadata,
      fetchValue,
      storage,
      true
    );

    return post(bundle);
  }
}
